import {
  CREATING_SKILL_FAILED,
  CREATING_SKILL_REQUEST,
  CREATING_SKILL_SUCCEEDED,
  DELETING_SKILL_FAILED,
  DELETING_SKILL_REQUEST,
  DELETING_SKILL_SUCCEEDED,
  FETCHING_SKILLS_FAILED,
  FETCHING_SKILLS_REQUEST,
  FETCHING_SKILLS_SUCCEEDED,
  INVALIDATE_CREATE_SKILL,
  INVALIDATE_DELETE_SKILL,
  INVALIDATE_SKILLS,
  SEARCHING_SKILLS_FAILED,
  SEARCHING_SKILLS_REQUEST,
  SEARCHING_SKILLS_SUCCEEDED,
} from "src/redux/actions/actionTypes";
import _ from "lodash";
import { Skill } from "../../../types/coaching/courses";
import { INVALIDATE_UPDATE_SKILL, UPDATING_SKILL_FAILED, UPDATING_SKILL_REQUEST, UPDATING_SKILL_SUCCEEDED } from "../../actions/actionTypes";
import { CreateSkillResponse, DeleteSkillResponse, UpdateSkillResponse } from "../../../types/coaching/skills";
import { orderBy } from "natural-orderby";


const updateSkillInternalId = (skill: Skill): Skill => {
  if (!skill || !skill.id) {
    return skill;
  }

  const updated = { ...skill };

  if (skill.id && !skill.uuid) {
    updated.uuid = skill.id; // Ensure uuid is set if id exists
  }

  if (_.size(skill.children) > 0) {
    updated.children = skill.children.map((child: any) => {
      if (child.id && !child.uuid) {
        child.uuid = child.id; // Ensure uuid is set for children as well
      }
      return child;
    });
  }

  return updated;
}


interface SkillCreationState {
  creating: boolean,
  created: boolean,
  type: string | null,
  item: Skill | null,
  error: any
}

interface SkillUpdateState {
  id: string,
  updating: boolean,
  updated: boolean,
  item: Skill | null,
  error: any
}

interface SkillDeleteState {
  id: string,
  deleting: boolean,
  deleted: boolean,
  error: any
}

interface SkillsState {
  loading: boolean;
  loaded: boolean;
  items: any[];
  searchResults: any[];
  pagination: {
    currentPage: number;
    totalPages: number;
    recordsPerPage: number;
    totalRecordsCount: number;
    hasMoreItems: boolean;
  };
  error: any;
  creation: SkillCreationState | null;
  update: SkillUpdateState | null;
  deletion: SkillDeleteState | null;
}

const initialState: SkillsState = {
  loading: false,
  loaded: false,
  items: [],
  searchResults: [],
  pagination: {
    currentPage: 0,
    totalPages: 0,
    recordsPerPage: 0,
    totalRecordsCount: 0,
    hasMoreItems: false,
  },
  error: null,
  creation: null,
  update: null,
  deletion: null,
};

export function skillsReducer(state = initialState, action: any) {
  switch (action.type) {

    case FETCHING_SKILLS_REQUEST: {
      return {
        ...state,
        loading: true,
        loaded: false,
        error: null,
      };
    }

    case FETCHING_SKILLS_SUCCEEDED: {

      const { response } = action.payload as any;
      const existingItems = state.items || [];

      let records = response.records;
      if (response.currentPage > 0) {
        records = [...existingItems, ...records];
      }

      records = _.uniqBy(records, "id");
      records = orderBy(records, [skill => (skill as any).name], "asc");
      records = records.map((item: any) => {
        const updated = updateSkillInternalId(item);
        return updated;
      });

      return {
        ...state,
        loading: false,
        loaded: true,
        items: records,
        pagination: {
          currentPage: response.currentPage,
          totalPages: response.totalPages,
          recordsPerPage: response.recordsPerPage,
          totalRecordsCount: response.totalRecordsCount,
          hasMoreItems: response.currentPage < response.totalPages - 1,
        },
        error: null,
      };
    }

    case FETCHING_SKILLS_FAILED: {

      return {
        ...state,
        loading: false,
        loaded: false,
        error: action.payload.error,
      };
    }

    case SEARCHING_SKILLS_REQUEST: {

      return {
        ...state,
        loading: true,
        loaded: false,
        error: null,
      };
    }

    case SEARCHING_SKILLS_SUCCEEDED: {

      const { response } = action.payload as any;
      const existingItems = state.searchResults || [];

      let records = response.records;
      if (response.currentPage > 0) {
        records = [...existingItems, ...records];
      }

      records = _.uniqBy(records, "id");
      records = orderBy(records, [skill => (skill as any).name], "asc");

      return {
        ...state,
        loading: false,
        loaded: true,
        searchResults: records,
        pagination: {
          currentPage: response.currentPage,
          totalPages: response.totalPages,
          recordsPerPage: response.recordsPerPage,
          totalRecordsCount: response.totalRecordsCount,
          hasMoreItems: response.currentPage < response.totalPages - 1,
        },
        error: null
      }
    }

    case SEARCHING_SKILLS_FAILED: {

      return {
        ...state,
        loading: false,
        loaded: false,
        error: action.payload.error,
      }
    }

    case INVALIDATE_SKILLS: {
      return {
        ...state,
        loading: false,
        loaded: false,
        items: [],
        searchResults: [],
        pagination: {
          currentPage: 0,
          totalPages: 0,
          recordsPerPage: 0,
          totalRecordsCount: 0,
          hasMoreItems: false,
        },
        error: null,
      };
    }

    case CREATING_SKILL_REQUEST: {
      return {
        ...state,
        creation: {
          creating: true,
          created: false,
          item: null,
          error: null
        }
      };
    }

    case CREATING_SKILL_SUCCEEDED: {
      const { skill } = action.payload as CreateSkillResponse;

      if (!state.creation) {
        return state;
      }

      const updatedSkill = updateSkillInternalId(skill);
      let records = [...state.items, updatedSkill]
      records = orderBy(records, [skill => (skill as any).name], "asc");

      return {
        ...state,
        creation: {
          creating: false,
          created: true,
          item: skill,
          error: null
        },
        items: records
      };
    }

    case CREATING_SKILL_FAILED: {
      if (!state.creation) {
        return state;
      }

      return {
        ...state,
        creation: {
          creating: false,
          created: false,
          item: null,
          error: action.payload.error
        }
      };
    }

    case INVALIDATE_CREATE_SKILL: {
      return {
        ...state,
        creation: null
      };
    }

    case UPDATING_SKILL_REQUEST: {
      const { id } = action.payload as any;
      return {
        ...state,
        update: {
          id,
          updating: true,
          updated: false,
          item: null,
          error: null
        }
      };
    }

    case UPDATING_SKILL_SUCCEEDED: {
      const { skill } = action.payload as UpdateSkillResponse;

      if (!state.update) {
        return state;
      }

      let updatedItems = state.items.map((item) => {
        if (item.id === skill.id) {
          const updatedSkill = updateSkillInternalId(skill);
          return { ...item, ...updatedSkill };
        }
        return item;
      });

      updatedItems = orderBy(updatedItems, [skill => (skill as any).name], "asc");

      return {
        ...state,
        update: {
          id: skill.id,
          updating: false,
          updated: true,
          item: skill,
          error: null
        },
        items: updatedItems
      };
    }

    case UPDATING_SKILL_FAILED: {
      if (!state.update) {
        return state;
      }

      return {
        ...state,
        update: {
          id: action.payload.id,
          updating: false,
          updated: false,
          item: null,
          error: action.payload.error
        }
      };
    }

    case INVALIDATE_UPDATE_SKILL: {
      return {
        ...state,
        update: null
      };
    }

    case DELETING_SKILL_REQUEST: {
      const { id } = action.payload as any;
      return {
        ...state,
        deletion: {
          id,
          deleting: true,
          deleted: false,
          item: null,
          error: null
        }
      };
    }

    case DELETING_SKILL_SUCCEEDED: {
      const { id } = action.payload as DeleteSkillResponse;

      if (!state.deletion) {
        return state;
      }

      const updatedItems = state.items.filter((item) => {
        return item.id !== id;
      });

      return {
        ...state,
        deletion: {
          id: id,
          deleting: false,
          deleted: true,
          error: null
        },
        items: updatedItems
      };
    }

    case DELETING_SKILL_FAILED: {
      if (!state.deletion) {
        return state;
      }

      return {
        ...state,
        deletion: {
          id: action.payload.id,
          deleting: false,
          deleted: false,
          error: action.payload.error
        }
      };
    }

    case INVALIDATE_DELETE_SKILL: {
      return {
        ...state,
        deletion: null
      };
    }

    default:
      return state;
  }
}
